import React from 'react';
import styled from 'styled-components';
import { Pencil } from 'lucide-react';
import { Check } from 'lucide-react';
import type { DocumentDate, DocumentTaxonomy, TaxonomyTreeNode } from '../../types';
import { SummaryAction } from '../DocumentSummaryHeader';
import { InlineEditableText } from '../InlineEditableText';
import { Breadcrumb, type BreadcrumbItem } from '../shared/Breadcrumb';
import { DocumentDatePicker } from '../DocumentDatePicker';
import { formatDocumentDate } from '../../utils/format';
import { getToneBackground, getToneForeground } from '../../theme/tone';

export interface InspectHeaderAction extends SummaryAction {
    shortcut?: string;
}

export interface DocumentInspectHeaderProps {
    documentTitle: string;
    documentId: string;
    /** Case-level breadcrumb (e.g. Cases / <case>), rendered inline at the
     *  left of the compact header row. The document title itself follows as
     *  the editable element rather than a breadcrumb item. */
    breadcrumb?: BreadcrumbItem[];
    /** Flat classification, rendered as the category pill with inline
     * edit/confirm affordances. */
    taxonomy?: DocumentTaxonomy | null;
    date?: DocumentDate;
    /** Total page count for this document. Rendered after the date as "· N pages". */
    pageCount?: number;
    actions: InspectHeaderAction[];
    activeEditors?: string[];
    currentEditorId?: string;
    onRenameSubmit?: (value: string) => void | Promise<void>;
    /** Title human-confirmation provenance (confirmed/confirmedBy), parallel to
     * date/taxonomy. One of the three confirmations that derive review status. */
    titleConfirmation?: { confirmed: boolean; confirmedBy?: string | null } | null;
    /** Confirm the current title as already correct (review), without renaming. */
    onTitleConfirm?: () => void | Promise<void>;
    onDateChange?: (date: DocumentDate | null) => void | Promise<void>;
    /** Confirm the current date as already correct (review). Mirrors
     * onCategoryConfirm; records the reviewer canonically server-side. */
    onDateConfirm?: () => void | Promise<void>;
    /** Section -> leaf tree for the category picker (same source as the table
     * filter). When provided alongside onCategoryChange, the chip becomes editable. */
    categoryTree?: TaxonomyTreeNode[];
    /** Apply a manual category override (an edit is implicitly correct). */
    onCategoryChange?: (next: { section: string; leaf?: string | null; subLeaf?: string | null }) => void | Promise<void>;
    /** Confirm the current auto category as already correct (review). */
    onCategoryConfirm?: () => void | Promise<void>;
    isTitleSaving?: boolean;
    isDuplicateOfOtherDoc?: boolean;
    duplicateOfRatio?: number | null;
    duplicatePageCount?: number;
    onDuplicateBadgeClick?: () => void;
}

/* ── Layout ────────────────────────────────────────────────── */

/* Single compact row (~32px): breadcrumb · title · date · category · page
   count · badges, with the editing indicator + actions pushed to the far
   right. Everything that used to stack (title block, chip row, page-count
   line) now flows inline; the title is the only element allowed to shrink. */
const Header = styled.header`
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 32px;
    min-width: 0;
`;

const BreadcrumbSlot = styled.div`
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 6px;
`;

/* Separator between the case breadcrumb and the document title; matches the
   Separator styling inside shared/Breadcrumb. */
const BreadcrumbSeparator = styled.span`
    flex-shrink: 0;
    font-size: 13px;
    color: #ddd;
`;

/* ── Title + DID ───────────────────────────────────────────── */

const TitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
    flex: 0 1 auto;
`;

const TitleText = styled.span`
    font-size: 15px;
    font-weight: 600;
    color: #111;
    letter-spacing: -0.02em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const RenameButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 4px;
    border: none;
    background: transparent;
    color: #aaa;
    cursor: pointer;
    flex-shrink: 0;

    &:hover {
        background: #f0f0f0;
        color: #555;
    }
`;

/* Green check once the title is human-confirmed (mirrors PillConfirmed on the
   date/category pills). The title isn't a pill, so this sits inline in the row. */
const TitleConfirmed = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    color: #16a34a;
`;

/* ── Meta: date + category pills (inline in the header row) ── */

const MetaGroup = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
    flex-shrink: 0;
`;

/* A tinted pill that visually CONTAINS a value plus its edit/confirm icons, so
   the affordances read as part of the field rather than buttons beside it.
   $tone drives the background (matches the shared TaxonomyChip palette). */
const Pill = styled.span<{ $tone: string }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 8px 3px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 500;
    background: ${({ $tone }) => getToneBackground($tone)};
    color: ${({ $tone }) => getToneForeground($tone)};
    max-width: 100%;
    min-width: 0;
`;

/* The pill's value text, ellipsized so a long label can't blow out the row. */
const PillLabel = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
`;

/* Icon-only ghost button living INSIDE a pill (edit / confirm). Tooltip via the
   native title attribute; no visible text. */
const PillIconButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: inherit;
    opacity: 0.65;
    cursor: pointer;

    &:hover:not(:disabled) {
        opacity: 1;
        background: rgba(0, 0, 0, 0.08);
    }
    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }
`;

/* Confirmed marker inside a pill: a green check, no text (tooltip carries the
   reviewer attribution). */
const PillConfirmed = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    color: #16a34a;
`;

const CategorySelect = styled.select`
    font-size: 12px;
    font-family: inherit;
    padding: 3px 6px;
    border: 1px solid #d0d0d0;
    border-radius: 6px;
    max-width: 280px;
`;

/* Small ghost text button (used only for the inline-editor Cancel action). */
const GhostButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: none;
    border: none;
    padding: 2px 4px;
    font-size: 12px;
    color: #888;
    cursor: pointer;
    font-family: inherit;
    border-radius: 4px;

    &:hover:not(:disabled) {
        background: #f0f0f0;
        color: #555;
    }
    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

/* ── Inline status bits ────────────────────────────────────── */

const MutedIndicator = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: #999;
    white-space: nowrap;
    flex-shrink: 0;
`;

const Spacer = styled.div`
    flex: 1 1 auto;
    min-width: 8px;
`;

const EditingIndicator = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    color: #888;
    font-weight: 500;
`;

const EditingDot = styled.span`
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #22c55e;
`;


/* ── Actions ───────────────────────────────────────────────── */

const Actions = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
    flex-shrink: 0;
`;

const ActionButton = styled.button<{ $variant: 'primary' | 'secondary' }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: ${({ $variant }) => ($variant === 'primary' ? '7px 14px' : '6px 12px')};
    border-radius: 6px;
    border: ${({ $variant }) => ($variant === 'primary' ? 'none' : '1px solid #e0e0e0')};
    background: ${({ $variant }) => ($variant === 'primary' ? '#111' : '#fff')};
    color: ${({ $variant }) => ($variant === 'primary' ? '#fff' : '#555')};
    font-size: ${({ $variant }) => ($variant === 'primary' ? '13px' : '12px')};
    font-weight: 500;
    cursor: pointer;

    &:hover:not(:disabled) {
        opacity: 0.85;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

/* ── Component ─────────────────────────────────────────────── */

export const DocumentInspectHeader: React.FC<DocumentInspectHeaderProps> = ({
    documentTitle,
    breadcrumb,
    taxonomy,
    date,
    pageCount,
    actions,
    activeEditors = [],
    currentEditorId,
    onRenameSubmit,
    titleConfirmation,
    onTitleConfirm,
    onDateChange,
    onDateConfirm,
    categoryTree = [],
    onCategoryChange,
    onCategoryConfirm,
    isTitleSaving = false,
    // Duplicate-badge props (isDuplicateOfOtherDoc / duplicateOfRatio /
    // duplicatePageCount / onDuplicateBadgeClick) remain in the interface but
    // are unused: the duplicate badge is intentionally hidden.
}) => {
    const [isEditingTitle, setIsEditingTitle] = React.useState(false);
    const [isTitleConfirming, setIsTitleConfirming] = React.useState(false);

    /* Category editing */
    const [isEditingCategory, setIsEditingCategory] = React.useState(false);
    const [isCategorySaving, setIsCategorySaving] = React.useState(false);
    const canEditCategory = Boolean(onCategoryChange && categoryTree.length > 0);

    /* The taxonomy is flat: each option's value is a bare category id (no
     * "section/leaf"). leaf/subLeaf are always null. (split('/') is harmless if a
     * value ever carried a leaf again — leaf would just be undefined -> null.) */
    const handleCategorySelect = React.useCallback(
        async (value: string) => {
            if (!onCategoryChange || !value) {
                setIsEditingCategory(false);
                return;
            }
            const [section, leaf] = value.split('/');
            setIsCategorySaving(true);
            try {
                await onCategoryChange({ section, leaf: leaf ?? null, subLeaf: null });
                setIsEditingCategory(false);
            } finally {
                setIsCategorySaving(false);
            }
        },
        [onCategoryChange],
    );

    const handleCategoryConfirm = React.useCallback(async () => {
        if (!onCategoryConfirm) return;
        setIsCategorySaving(true);
        try {
            await onCategoryConfirm();
        } finally {
            setIsCategorySaving(false);
        }
    }, [onCategoryConfirm]);

    /* Flat category list: one <option> per category, no optgroups/leaves. */
    const categoryOptions = React.useMemo(
        () =>
            categoryTree.map(section => (
                <option key={section.value} value={section.value}>
                    {section.label}
                </option>
            )),
        [categoryTree],
    );

    /* Hover tooltip for the category pill. The model-certainty callout was removed
     * from the visible row, but the (uncalibrated) signal still rides along here. */
    const categoryTooltip = React.useMemo(() => {
        if (!taxonomy) return undefined;
        if (taxonomy.classificationFailed) {
            return 'Automatic classification failed for this document — it needs manual review.';
        }
        const base = taxonomy.displayLabel;
        if (taxonomy.confirmed) return base;
        if (taxonomy.certainty) {
            return `${base} · model certainty: ${taxonomy.certainty} ` +
                '(how committed the model was — not a probability of being correct)';
        }
        return base;
    }, [taxonomy]);

    /* Presence */
    const isEditing = React.useMemo(() => {
        if (!activeEditors.length && !currentEditorId) return false;
        return true;
    }, [activeEditors, currentEditorId]);

    /* Date */
    const canEditDate = Boolean(onDateChange);
    const [isDateOpen, setIsDateOpen] = React.useState(false);
    const [isDateSaving, setIsDateSaving] = React.useState(false);
    const dateAnchorRef = React.useRef<HTMLButtonElement>(null);
    // Display the stored value at its precision (never padded to a fabricated day).
    const formattedDate = date && !date.noDate ? formatDocumentDate(date) : null;

    // The shared popup owns precision + no-date; it hands back a finished
    // DocumentDate (or the no-date sentinel, or null). We just persist it.
    const handleDateCommit = async (next: DocumentDate | null) => {
        if (!onDateChange || isDateSaving) return;
        setIsDateSaving(true);
        try {
            await onDateChange(next);
        } finally {
            setIsDateSaving(false);
        }
    };

    const handleDateConfirm = async () => {
        if (!onDateConfirm || isDateSaving) return;
        setIsDateSaving(true);
        try {
            await onDateConfirm();
        } finally {
            setIsDateSaving(false);
        }
    };

    const handleTitleSubmit = React.useCallback(
        async (nextValue: string) => {
            await onRenameSubmit?.(nextValue);
            setIsEditingTitle(false);
        },
        [onRenameSubmit],
    );

    const handleTitleConfirm = React.useCallback(async () => {
        if (!onTitleConfirm) return;
        setIsTitleConfirming(true);
        try {
            await onTitleConfirm();
        } finally {
            setIsTitleConfirming(false);
        }
    }, [onTitleConfirm]);

    const titleConfirmed = Boolean(titleConfirmation?.confirmed);
    const titleBusy = isTitleSaving || isTitleConfirming;

    return (
        <Header>
            {breadcrumb && breadcrumb.length > 0 && (
                <BreadcrumbSlot>
                    <Breadcrumb items={breadcrumb} />
                    <BreadcrumbSeparator aria-hidden="true">/</BreadcrumbSeparator>
                </BreadcrumbSlot>
            )}
            <TitleRow>
                {isEditingTitle && onRenameSubmit ? (
                    <InlineEditableText
                        value={documentTitle}
                        onSubmit={handleTitleSubmit}
                        onCancel={() => setIsEditingTitle(false)}
                        isSaving={isTitleSaving}
                        autoFocus
                        ariaLabel="Document title"
                    />
                ) : (
                    <>
                        <TitleText title={documentTitle}>{documentTitle}</TitleText>
                        {onRenameSubmit && (
                            <RenameButton
                                type="button"
                                onClick={() => setIsEditingTitle(true)}
                                disabled={titleBusy}
                                aria-label="Rename document"
                            >
                                <Pencil size={14} />
                            </RenameButton>
                        )}
                        {titleConfirmed ? (
                            <TitleConfirmed
                                title={titleConfirmation?.confirmedBy ? `Confirmed by ${titleConfirmation.confirmedBy}` : 'Title confirmed by a reviewer'}
                                aria-label={titleConfirmation?.confirmedBy ? `Confirmed by ${titleConfirmation.confirmedBy}` : 'Title confirmed'}
                            >
                                <Check size={16} />
                            </TitleConfirmed>
                        ) : (
                            onTitleConfirm && documentTitle?.trim() && (
                                <RenameButton
                                    type="button"
                                    onClick={handleTitleConfirm}
                                    disabled={titleBusy}
                                    title="Confirm title"
                                    aria-label="Confirm title"
                                >
                                    <Check size={16} />
                                </RenameButton>
                            )
                        )}
                    </>
                )}
            </TitleRow>

            {/* Date + category pills, inline. Each pill CONTAINS its value plus
                icon-only edit/confirm affordances (tooltips via title). */}
            <MetaGroup>
                {/* ── Date pill ── */}
                {canEditDate ? (
                    <Pill $tone="default">
                        <PillLabel>{formattedDate ?? 'No date'}</PillLabel>
                        {/* Precision (Y/M/D) and the "No date" option now live
                            INSIDE the shared popup, opened by this pencil — so
                            the inspector and table edit dates identically. */}
                        <PillIconButton
                            ref={dateAnchorRef}
                            type="button"
                            onClick={() => setIsDateOpen(true)}
                            disabled={isDateSaving}
                            title={formattedDate || date?.noDate ? 'Change date' : 'Set date'}
                            aria-label={formattedDate || date?.noDate ? 'Change date' : 'Set date'}
                        >
                            <Pencil size={13} />
                        </PillIconButton>
                        {date?.confirmed ? (
                            <PillConfirmed
                                title={date.confirmedBy ? `Confirmed by ${date.confirmedBy}` : 'Date confirmed by a reviewer'}
                                aria-label={date.confirmedBy ? `Confirmed by ${date.confirmedBy}` : 'Date confirmed'}
                            >
                                <Check size={14} />
                            </PillConfirmed>
                        ) : (
                            onDateConfirm && (formattedDate || date?.noDate) && (
                                <PillIconButton
                                    type="button"
                                    onClick={handleDateConfirm}
                                    disabled={isDateSaving}
                                    title="Confirm date"
                                    aria-label="Confirm date"
                                >
                                    <Check size={14} />
                                </PillIconButton>
                            )
                        )}
                        <DocumentDatePicker
                            date={date}
                            open={isDateOpen}
                            onOpen={() => setIsDateOpen(true)}
                            onClose={() => setIsDateOpen(false)}
                            anchorEl={dateAnchorRef.current}
                            disabled={isDateSaving}
                            onChange={handleDateCommit}
                        />
                    </Pill>
                ) : (formattedDate || date?.noDate) ? (
                    <Pill $tone="default">
                        <PillLabel>{formattedDate ?? 'No date'}</PillLabel>
                    </Pill>
                ) : null}

                {/* ── Category pill ── */}
                {isEditingCategory ? (
                    <>
                        <CategorySelect
                            autoFocus
                            disabled={isCategorySaving}
                            defaultValue={
                                taxonomy
                                    ? (taxonomy.leaf && taxonomy.leaf !== 'unspecified'
                                        ? `${taxonomy.section}/${taxonomy.leaf}`
                                        : taxonomy.section)
                                    : ''
                            }
                            onChange={e => handleCategorySelect(e.target.value)}
                        >
                            {!taxonomy && (
                                <option value="" disabled>
                                    Select a category…
                                </option>
                            )}
                            {categoryOptions}
                        </CategorySelect>
                        <GhostButton
                            type="button"
                            onClick={() => setIsEditingCategory(false)}
                            disabled={isCategorySaving}
                        >
                            Cancel
                        </GhostButton>
                    </>
                ) : taxonomy ? (
                    <Pill $tone={taxonomy.classificationFailed ? 'warning' : 'info'}>
                        <PillLabel title={categoryTooltip}>
                            {taxonomy.classificationFailed
                                ? 'Needs review — not classified'
                                : taxonomy.displayLabel}
                        </PillLabel>
                        {canEditCategory && (
                            <PillIconButton
                                type="button"
                                onClick={() => setIsEditingCategory(true)}
                                disabled={isCategorySaving}
                                title="Change category"
                                aria-label="Change category"
                            >
                                <Pencil size={13} />
                            </PillIconButton>
                        )}
                        {taxonomy.confirmed ? (
                            <PillConfirmed
                                title={taxonomy.confirmedBy ? `Confirmed by ${taxonomy.confirmedBy}` : 'Category confirmed by a reviewer'}
                                aria-label={taxonomy.confirmedBy ? `Confirmed by ${taxonomy.confirmedBy}` : 'Category confirmed'}
                            >
                                <Check size={14} />
                            </PillConfirmed>
                        ) : (
                            onCategoryConfirm && !taxonomy.classificationFailed && (
                                <PillIconButton
                                    type="button"
                                    onClick={handleCategoryConfirm}
                                    disabled={isCategorySaving}
                                    title="Confirm category"
                                    aria-label="Confirm category"
                                >
                                    <Check size={14} />
                                </PillIconButton>
                            )
                        )}
                    </Pill>
                ) : (
                    canEditCategory && (
                        <GhostButton
                            type="button"
                            onClick={() => setIsEditingCategory(true)}
                            disabled={isCategorySaving}
                        >
                            <Pencil size={12} /> Set category
                        </GhostButton>
                    )
                )}
            </MetaGroup>

            {pageCount != null && (
                <MutedIndicator>
                    {pageCount} {pageCount === 1 ? 'page' : 'pages'}
                </MutedIndicator>
            )}
            {/* Duplicate-document badge intentionally hidden (detection and the
                duplicates view still work; only the header badge is gone). */}

            <Spacer />

            <Actions>
                {isEditing && (
                    <EditingIndicator>
                        <EditingDot />
                        Editing
                    </EditingIndicator>
                )}
                {actions.map(action => (
                    <ActionButton
                        key={action.id}
                        $variant={action.variant ?? 'secondary'}
                        onClick={action.onClick}
                        disabled={action.disabled}
                    >
                        {action.icon}
                        <span>{action.label}</span>
                    </ActionButton>
                ))}
            </Actions>
        </Header>
    );
};
